import { isCssColour } from "@/utils/css-colour";

const WIDTHS = new Set(["sm", "md", "lg", "full"]);
const ALIGNMENTS = new Set(["left", "center", "right"]);
const THICKNESSES = new Set(["1", "2", "3", "4"]);
/** The palette roles, the look system's palette colours and the theme's colour variables: what the CMS colour picker names. */
const KNOWN_VARIABLE =
  /^--(?:re-(?:background|text|muted|surface|border|accent|accent-text)|re-p-[a-z0-9-]+|[a-z][a-z0-9-]*-color)$/;

export const isRichTextRule = (
  attributes: Record<string, string | undefined>,
) => attributes["data-re-rule"] === "1";

/**
 * A colour isCssColour accepts whose var() names a known variable; otherwise undefined, and rich-text.css's default applies.
 * Named colours and keywords (`red`, `transparent`, `currentColor`) are not drawn. b2b-apps' rich-editor-rule.ts copies
 * this check, with isCssColour and KNOWN_VARIABLE, so the editor stores and draws only what the website draws: change both.
 */
export function richTextRuleColour(value?: string): string | undefined {
  const colour = value?.trim();
  if (!isCssColour(colour)) return;
  for (const [, name] of colour.matchAll(/var\(\s*(--[\w-]+)/gi))
    if (!KNOWN_VARIABLE.test(name)) return;
  return colour;
}

/** A stored style or class never reaches the line; an invalid value is left out and rich-text.css draws its default. */
export function richTextRuleProps(
  attributes: Record<string, string | undefined>,
): Record<string, unknown> {
  const props: Record<string, unknown> = { "data-re-rule": "1" };
  for (const [name, values] of [
    ["data-width", WIDTHS],
    ["data-align", ALIGNMENTS],
    ["data-thickness", THICKNESSES],
  ] as const) {
    const value = attributes[name];
    if (value !== undefined && values.has(value)) props[name] = value;
  }
  const colour = richTextRuleColour(attributes["data-color"]);
  if (colour) {
    props["data-color"] = colour;
    props.style = { "--re-rich-rule-color": colour };
  }
  return props;
}

const HR_TAG =
  /<hr\b((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)(\s*\/?>)/gi;
const ATTRIBUTE =
  /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

/**
 * For components that draw stored HTML as it is: CSS draws a line's width, alignment and thickness from its attributes,
 * but its colour only through the variable RichTextContent sets. HTML without a styled line is returned unchanged.
 */
export function withRichTextRuleColours(html: string): string {
  if (typeof html !== "string" || !/data-re-rule/i.test(html)) return html;
  return html.replace(HR_TAG, (tag, attributes: string, end: string) => {
    const parsed: Record<string, string> = {};
    for (const [, name, double, single, bare] of attributes.matchAll(ATTRIBUTE))
      parsed[name.toLowerCase()] = double ?? single ?? bare ?? "";
    if (!isRichTextRule(parsed) || "style" in parsed) return tag;
    const colour = richTextRuleColour(parsed["data-color"]);
    return colour
      ? `${tag.slice(0, 3)}${attributes} style="--re-rich-rule-color:${colour}"${end}`
      : tag;
  });
}
